// Filter-type registry for the DataTable filter panel.
//
// A filter *field* is pure description:
//   { key, label, type, options?, trueLabel?, falseLabel?, defaultExpanded?, apiKey?, apiRange?,
//     placeholder?, emptyText? }
// type: 'multiselect' | 'enum' | 'text' | 'boolean' | 'date' | 'number'
//
// Each descriptor knows how a value of its type is stored in the URL (so filtered views
// are linkable), how it is sent to the API (lib/paginate.js filter_* conventions), how it
// reads on a chip, and when it counts as empty.
//
// URL keys:  multiselect  ?status=1,0          enum/text/boolean  ?key=value
//            date         ?created_from=…&created_to=…   number  ?failures_min=…&failures_max=…
// API keys:  filter_<apiKey|key>, with _from/_to or _min/_max for ranges; a field can name its
//            range params outright with apiRange: ['filter_from_date', 'filter_to_date'].
import { formatDate } from './format';

/** "No tenant" sentinel understood by the API (lib/paginate.js NONE). */
export const NONE = '__none__';

const blank = (v) => v === undefined || v === null || v === '';
const apiName = (field) => `filter_${field.apiKey || field.key}`;

const optionLabel = (field, value) => {
    const hit = (field.options || []).find((o) => String(o.value) === String(value));
    if (hit) return hit.label;
    return field.labelOf ? field.labelOf(value) : null;
};

const listLabel = (field, values) => {
    const labels = values.map((v) => optionLabel(field, v));
    // Never show raw ids: while options are loading (or a value is unknown) fall back to a count.
    if (labels.some((l) => l === null)) return `${values.length} selected`;
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
};

const rangeDescriptor = (lo, hi, format) => ({
    isEmpty: (v) => !v || (blank(v[lo]) && blank(v[hi])),
    fromUrl: (sp, key) => {
        const v = { [lo]: sp.get(`${key}_${lo}`) || '', [hi]: sp.get(`${key}_${hi}`) || '' };
        return blank(v[lo]) && blank(v[hi]) ? undefined : v;
    },
    toUrl: (sp, key, v) => {
        [lo, hi].forEach((end) => {
            if (v && !blank(v[end])) sp.set(`${key}_${end}`, v[end]);
            else sp.delete(`${key}_${end}`);
        });
    },
    urlKeys: (key) => [`${key}_${lo}`, `${key}_${hi}`],
    toApi: (field, v) => {
        const out = {};
        const [loKey, hiKey] = field.apiRange || [`${apiName(field)}_${lo}`, `${apiName(field)}_${hi}`];
        if (!blank(v[lo])) out[loKey] = v[lo];
        if (!blank(v[hi])) out[hiKey] = v[hi];
        return out;
    },
    formatChip: (field, v) => format(v[lo], v[hi], field),
});

const scalar = {
    isEmpty: blank,
    fromUrl: (sp, key) => sp.get(key) || undefined,
    toUrl: (sp, key, v) => (blank(v) ? sp.delete(key) : sp.set(key, String(v))),
    urlKeys: (key) => [key],
    toApi: (field, v) => ({ [apiName(field)]: String(v).trim() }),
};

const DESCRIPTORS = {
    multiselect: {
        isEmpty: (v) => !Array.isArray(v) || v.length === 0,
        fromUrl: (sp, key) => {
            const raw = sp.get(key);
            if (!raw) return undefined;
            const values = [...new Set(raw.split(',').filter(Boolean))];
            return values.length ? values : undefined;
        },
        toUrl: (sp, key, v) => (Array.isArray(v) && v.length ? sp.set(key, v.join(',')) : sp.delete(key)),
        urlKeys: (key) => [key],
        toApi: (field, v) => ({ [apiName(field)]: v.join(',') }),
        formatChip: (field, v) => listLabel(field, v),
    },
    enum: {
        ...scalar,
        formatChip: (field, v) => optionLabel(field, v) ?? '1 selected',
    },
    boolean: {
        ...scalar,
        fromUrl: (sp, key) => {
            const v = sp.get(key);
            return v === '1' || v === '0' ? v : undefined;
        },
        formatChip: (field, v) => (v === '1' ? field.trueLabel || 'Yes' : field.falseLabel || 'No'),
    },
    text: {
        ...scalar,
        formatChip: (field, v) => `contains “${v}”`,
    },
    date: rangeDescriptor('from', 'to', (from, to) => {
        if (from && to) return from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
        return from ? `from ${formatDate(from)}` : `until ${formatDate(to)}`;
    }),
    number: rangeDescriptor('min', 'max', (min, max) => {
        const n = (x) => Number(x).toLocaleString();
        if (!blank(min) && !blank(max)) return min === max ? n(min) : `${n(min)}–${n(max)}`;
        return !blank(min) ? `${n(min)} or more` : `${n(max)} or fewer`;
    }),
};

export const getDescriptor = (type) => DESCRIPTORS[type] || DESCRIPTORS.text;

export const isFilterEmpty = (field, value) => getDescriptor(field.type).isEmpty(value);

export const formatChip = (field, value) => getDescriptor(field.type).formatChip(field, value);

/** Values for every field, read from URLSearchParams. Missing fields are absent. */
export const readFilters = (fields, sp) => {
    const out = {};
    fields.forEach((f) => {
        const v = getDescriptor(f.type).fromUrl(sp, f.key);
        if (v !== undefined) out[f.key] = v;
    });
    return out;
};

/** Writes (or removes, when empty) one field's value into URLSearchParams in place. */
export const writeFilter = (field, sp, value) => {
    const d = getDescriptor(field.type);
    if (d.isEmpty(value)) d.urlKeys(field.key).forEach((k) => sp.delete(k));
    else d.toUrl(sp, field.key, value);
};

/** API query params (filter_*) for the active values. */
export const toApiParams = (fields, values) => {
    const out = {};
    fields.forEach((f) => {
        const v = values[f.key];
        const d = getDescriptor(f.type);
        if (!d.isEmpty(v)) Object.assign(out, d.toApi(f, v));
    });
    return out;
};

export const countActive = (fields, values) => fields.filter((f) => !isFilterEmpty(f, values[f.key])).length;

/** Every URL key the fields own (for "Clear all"). */
export const urlKeysOf = (fields) => fields.flatMap((f) => getDescriptor(f.type).urlKeys(f.key));
